// ---------------------------------------------------------------
// Teltonika device-type resolver.
// The IMEI/TAC does NOT reliably distinguish similar models (e.g. FMM00A vs
// FMM13A can share a TAC block), so we never GUESS the model from the IMEI.
// Accuracy comes from, best first:
//   1. A model you set by hand at intake (from the box label) — authoritative.
//   2. The model the unit reports to Traccar once it connects — ground truth.
//   3. A TAC map you have personally verified (optional, off by default).
// If none of those is known, we show "Set model" instead of a wrong guess.
// ---------------------------------------------------------------

// OPTIONAL, verified-only. First 8 digits of the IMEI -> model. Leave empty
// unless you've CONFIRMED a whole TAC block is one model. Guessing here is what
// caused the FMM00A/FMM13A mix-up, so it stays empty by default.
export const TELTONIKA_TAC = {
  // '________': 'FMM00A',  // only add codes you've verified
};

// Models you stock — powers the intake dropdown and the friendly descriptions.
// Add a SKU here and it appears in the picker automatically.
export const MODELS = {
  FMM00A: { kind: 'OBDII plug-in', form: 'Plugs into the OBD port' },
  FMM13A: { kind: 'Hardwired 4G', form: 'Wired to vehicle power' },
  FMC130: { kind: 'Hardwired 4G', form: 'Wired to vehicle power' },
  FMC150: { kind: 'Hardwired 4G', form: 'Wired to vehicle power' },
  FMB920: { kind: 'Hardwired 2G', form: 'Wired to vehicle power' },
  FMB003: { kind: 'OBDII plug-in', form: 'Plugs into the OBD port' },
  FMC001: { kind: 'OBDII plug-in', form: 'Plugs into the OBD port' },
  FMB125: { kind: 'Hardwired', form: 'Wired to vehicle power' },
  FMM130: { kind: 'Hardwired 4G', form: 'Wired to vehicle power' },
};

export const MODEL_LIST = Object.keys(MODELS);

// Normalize whatever the device/Traccar reports into a clean model token.
function normalizeModel(raw) {
  if (!raw) return null;
  const s = String(raw).toUpperCase().replace(/[\s_-]/g, '');
  const m = s.match(/FM[A-Z]?\d{2,3}[A-Z0-9]?/); // e.g. FMM00A / FMM13A / FMC130
  return m ? m[0] : String(raw).trim();
}

// device: a Traccar device object (has model, uniqueId, attributes)
// position (optional): its latest position, whose attributes may carry the model
export function resolveDeviceType(device, position) {
  const manual = normalizeModel(device?.attributes?.modelManual); // you set this at intake
  const reported = normalizeModel(
    device?.model
    || device?.attributes?.model
    || device?.attributes?.deviceModel
    || position?.attributes?.model
  );
  const tac = String(device?.uniqueId || '').slice(0, 8);
  const fromTac = TELTONIKA_TAC[tac] || null;

  const model = manual || reported || fromTac || null; // no blanket assumption
  const info = model ? MODELS[model] : null;
  const isTeltonika = !!model && /^FM/i.test(model);
  const source = manual ? 'manual' : reported ? 'reported' : fromTac ? 'imei' : 'unknown';

  return {
    model: model || null,                       // e.g. 'FMM00A' or null when unset
    label: model || 'Set model',                // display string
    kind: info ? info.kind : (isTeltonika ? 'Teltonika tracker' : ''),
    form: info ? info.form : '',
    source,                                     // manual | reported | imei | unknown
    isTeltonika,
  };
}
